import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Sparkles, 
  RefreshCw, 
  Loader2, 
  AlertTriangle, 
  AlertCircle, 
  Info,
  CheckCircle,
  Mail,
  ListTodo,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronDown,
  ChevronUp,
  Send,
  Copy,
  X
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${window.location.origin}/api`;

const alertIcons = {
  urgent: AlertTriangle,
  warning: AlertCircle,
  info: Info
};

const alertColors = {
  urgent: "bg-red-50 border-red-200 text-red-800",
  warning: "bg-amber-50 border-amber-200 text-amber-800",
  info: "bg-blue-50 border-blue-200 text-blue-800"
};

const trendIcons = {
  up: TrendingUp,
  down: TrendingDown,
  stable: Minus
};

export default function AIInsightsPanel({ authHeaders, context = "dashboard", collapsed = false, onTaskCreate }) {
  const [insights, setInsights] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!collapsed);
  const [isDraftEmailOpen, setIsDraftEmailOpen] = useState(false);
  const [draftEmailData, setDraftEmailData] = useState({ customer_name: '', customer_email: '', email_type: 'follow_up', context: '' });
  const [draftedEmail, setDraftedEmail] = useState(null);
  const [isDraftingEmail, setIsDraftingEmail] = useState(false);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/ai/business-intelligence?context=${context}`, authHeaders);
      setInsights(response.data);
    } catch (error) {
      console.error("Error fetching AI insights:", error);
      // Don't show error toast - graceful degradation
    } finally {
      setLoading(false);
    }
  }, [context, authHeaders]);

  const fetchAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/ai/alerts`, authHeaders);
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    } finally {
      setAlertsLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchAlerts();
    fetchInsights();
  }, [fetchAlerts, fetchInsights]);

  const handleDraftEmail = async () => {
    if (!draftEmailData.customer_name || !draftEmailData.customer_email) {
      toast.error("Please enter customer name and email");
      return;
    }
    setIsDraftingEmail(true);
    try {
      const response = await axios.post(`${API}/admin/ai/draft-email`, draftEmailData, authHeaders);
      setDraftedEmail(response.data.email);
      toast.success("Email drafted!");
    } catch (error) {
      console.error("Error drafting email:", error);
      toast.error("Failed to draft email");
    } finally {
      setIsDraftingEmail(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleCreateTask = (task) => {
    if (onTaskCreate) {
      onTaskCreate(task);
    } else {
      toast.info(`Task: ${task.task}`, { description: `Priority: ${task.priority}` });
    }
  };

  if (!isExpanded) {
    return (
      <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
        <CardContent className="py-3">
          <button 
            onClick={() => setIsExpanded(true)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-600" />
              <span className="font-medium text-violet-900">AI Insights</span>
              {alerts.length > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {alerts.filter(a => a.type === 'urgent').length || alerts.length} alerts
                </Badge>
              )}
            </div>
            <ChevronDown className="h-4 w-4 text-violet-600" />
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-600" />
              <CardTitle className="text-lg text-violet-900">AI Business Intelligence</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => setIsDraftEmailOpen(true)}
                className="text-violet-700 hover:text-violet-900 hover:bg-violet-100"
              >
                <Mail className="h-4 w-4 mr-1" />
                Draft Email
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => { fetchInsights(); fetchAlerts(); }}
                disabled={loading}
                className="text-violet-700 hover:text-violet-900 hover:bg-violet-100"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsExpanded(false)}
                className="text-violet-700 hover:text-violet-900 hover:bg-violet-100"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Alerts Section */}
          {alerts.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-violet-900 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Alerts ({alerts.length})
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {alerts.slice(0, 5).map((alert, idx) => {
                  const Icon = alertIcons[alert.type] || Info;
                  return (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg border ${alertColors[alert.type]} flex items-start gap-3`}
                    >
                      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{alert.title}</p>
                        <p className="text-xs mt-0.5 opacity-80">{alert.message}</p>
                        {alert.action && (
                          <p className="text-xs mt-1 font-medium">→ {alert.action}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quick Stats */}
          {insights?.data?.quick_stats?.highlight && (
            <div className="bg-white/60 rounded-lg p-3 border border-violet-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-violet-900">{insights.data.quick_stats.highlight}</p>
                  <p className="text-xs text-violet-600 mt-0.5">{insights.data.quick_stats.context}</p>
                </div>
                {insights.data.quick_stats.trend && (
                  <div className={`flex items-center gap-1 ${
                    insights.data.quick_stats.trend === 'up' ? 'text-green-600' : 
                    insights.data.quick_stats.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {(() => {
                      const TrendIcon = trendIcons[insights.data.quick_stats.trend] || Minus;
                      return <TrendIcon className="h-5 w-5" />;
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Insights */}
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
              <span className="ml-2 text-sm text-violet-600">Analyzing business data...</span>
            </div>
          ) : insights?.data?.insights?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-violet-900 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Insights
              </h4>
              {insights.data.insights.map((insight, idx) => (
                <div key={idx} className="bg-white/60 rounded-lg p-3 border border-violet-100">
                  <p className="text-sm text-violet-900">{insight.insight}</p>
                  {insight.recommendation && (
                    <p className="text-xs text-violet-600 mt-1 flex items-start gap-1">
                      <CheckCircle className="h-3 w-3 mt-0.5 shrink-0" />
                      {insight.recommendation}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Suggested Tasks */}
          {insights?.data?.suggested_tasks?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-violet-900 flex items-center gap-2">
                <ListTodo className="h-4 w-4" />
                Suggested Tasks
              </h4>
              <div className="space-y-2">
                {insights.data.suggested_tasks.map((task, idx) => (
                  <div 
                    key={idx} 
                    className="bg-white/60 rounded-lg p-3 border border-violet-100 flex items-center justify-between gap-2"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant={task.priority === 'high' ? 'destructive' : task.priority === 'medium' ? 'default' : 'secondary'}
                          className="text-[10px]"
                        >
                          {task.priority}
                        </Badge>
                        <span className="text-xs text-violet-500">{task.due}</span>
                      </div>
                      <p className="text-sm text-violet-900 mt-1">{task.task}</p>
                      {task.related_to && (
                        <p className="text-xs text-violet-600">Related to: {task.related_to}</p>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleCreateTask(task)}
                      className="shrink-0 text-xs"
                    >
                      Create
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw Metrics Footer */}
          {insights?.raw_metrics && (
            <div className="pt-2 border-t border-violet-200 flex flex-wrap gap-4 text-xs text-violet-600">
              <span>Customers: {insights.raw_metrics.total_customers}</span>
              <span>Revenue: £{insights.raw_metrics.total_revenue?.toLocaleString()}</span>
              <span>Pending Quotes: {insights.raw_metrics.pending_quotes}</span>
              <span>New Enquiries: {insights.raw_metrics.new_enquiries}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Draft Email Dialog */}
      <Dialog open={isDraftEmailOpen} onOpenChange={setIsDraftEmailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              AI Email Drafter
            </DialogTitle>
            <DialogDescription>
              Let AI draft a professional email for you to review and send.
            </DialogDescription>
          </DialogHeader>
          
          {!draftedEmail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer Name *</Label>
                  <input 
                    type="text"
                    className="w-full mt-1 px-3 py-2 border rounded-md"
                    placeholder="e.g., Gilbert Engineering"
                    value={draftEmailData.customer_name}
                    onChange={(e) => setDraftEmailData(p => ({ ...p, customer_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Customer Email *</Label>
                  <input 
                    type="email"
                    className="w-full mt-1 px-3 py-2 border rounded-md"
                    placeholder="customer@email.com"
                    value={draftEmailData.customer_email}
                    onChange={(e) => setDraftEmailData(p => ({ ...p, customer_email: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Email Type</Label>
                <Select 
                  value={draftEmailData.email_type} 
                  onValueChange={(v) => setDraftEmailData(p => ({ ...p, email_type: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="follow_up">Follow Up</SelectItem>
                    <SelectItem value="quote_reminder">Quote Reminder</SelectItem>
                    <SelectItem value="thank_you">Thank You</SelectItem>
                    <SelectItem value="booking_confirmation">Booking Confirmation</SelectItem>
                    <SelectItem value="job_update">Job Update</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Additional Context (Optional)</Label>
                <Textarea 
                  placeholder="Any specific details to include in the email..."
                  value={draftEmailData.context}
                  onChange={(e) => setDraftEmailData(p => ({ ...p, context: e.target.value }))}
                  className="mt-1"
                  rows={2}
                />
              </div>
              <Button 
                onClick={handleDraftEmail} 
                disabled={isDraftingEmail}
                className="w-full bg-violet-600 hover:bg-violet-700"
              >
                {isDraftingEmail ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Drafting...</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" />Draft Email with AI</>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Subject</Label>
                  <p className="font-medium">{draftedEmail.subject}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Body</Label>
                  <div className="mt-1 whitespace-pre-wrap text-sm bg-white p-3 rounded border">
                    {draftedEmail.body}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => copyToClipboard(`Subject: ${draftedEmail.subject}\n\n${draftedEmail.body}`)}
                  className="flex-1"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy to Clipboard
                </Button>
                <Button 
                  onClick={() => {
                    window.location.href = `mailto:${draftEmailData.customer_email}?subject=${encodeURIComponent(draftedEmail.subject)}&body=${encodeURIComponent(draftedEmail.body)}`;
                  }}
                  className="flex-1 bg-violet-600 hover:bg-violet-700"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Open in Email App
                </Button>
              </div>
              <Button 
                variant="ghost" 
                onClick={() => { setDraftedEmail(null); }}
                className="w-full"
              >
                Draft Another Email
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
